import {Card, FormControl, openDialog} from 'platform/components';
import {VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Locale} from '@dms/api/base';
import {PreviewCustomerMessages, TemplateContextEnum} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';
import {EmailLocaleTemplateGroup} from './EmailLocaleTemplateGroup';
import {EmailPreview} from './EmailPreview';

interface EmailChannelControlProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  context: TemplateContextEnum | Nullish;
  customerMessageId: PreviewCustomerMessages | null;
  locales: Locale[];
}

export function EmailChannelControl(props: EmailChannelControlProps) {
  const isMultiLocale = props.locales.length > 1;

  const isBodyOrSubjectError =
    isNotNilOrEmpty(props.formApi.formState.errors.emailBodies) ||
    isNotNilOrEmpty(props.formApi.formState.errors.emailSubjects);

  const [isEmailEnabled, isAutomatizationEnabled] = useWatch({
    control: props.control,
    name: ['emailEnabled', 'enabled'],
  });

  // Previews in the edited locale, not the operator's UI language — otherwise the
  // preview shows a different template than the customer is sent.
  const handlePreview = (locale: Locale) => {
    if (isNil(props.context) || isNil(props.customerMessageId)) {
      return;
    }

    openDialog(
      <EmailPreview
        body={props.formApi.getValues(`emailBodies.${locale}`) ?? ''}
        subject={props.formApi.getValues(`emailSubjects.${locale}`) ?? ''}
        locale={locale}
        customerMessageId={props.customerMessageId}
      />,
      {title: i18n.t('general.labels.emailPreview')}
    );
  };

  const isChannelDisabled = !isEmailEnabled || !isAutomatizationEnabled;

  return (
    <Card
      isExpandable
      isClosedByDefault
      variant="inlineWhite"
      colorStripe={isBodyOrSubjectError ? 'red' : undefined}
      title={i18n.t('general.labels.email')}
      control={{
        type: 'checkbox',
        value: isEmailEnabled,
        isDisabled: !isAutomatizationEnabled,
        onChange: (value) => {
          props.formApi.setValue('emailEnabled', value);

          if (!value) {
            props.formApi.clearErrors('emailBodies');
            props.formApi.clearErrors('emailSubjects');
            props.formApi.clearErrors('emailCredentialsId');
          }
        },
      }}
    >
      <VStack spacing={6}>
        {props.locales.map((locale) => (
          <EmailLocaleTemplateGroup
            key={locale}
            locale={locale}
            control={props.control}
            formApi={props.formApi}
            context={props.context}
            isChannelDisabled={isChannelDisabled}
            isPreviewDisabled={isNil(props.customerMessageId)}
            isLocaleHeadingVisible={isMultiLocale}
            onPreview={handlePreview}
          />
        ))}
      </VStack>
    </Card>
  );
}
